import {
  Row,
  Col,
  message,
  Button,
  Form,
  Input,
  InputNumber,
  Breadcrumb,
  Table,
  Tag,
  Divider,
  Popconfirm,
  Select,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useState, useCallback, useMemo } from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { MdEdit, MdDeleteOutline } from "react-icons/md";
import { routes } from "common/routes";
import { IVendorB2c, IVendorSkuMapping } from "common/interface/IVendorItems";
import {
  getVendorSkuMappings,
  createVendorSkuMapping,
  updateVendorSkuMappingReliability,
  deleteVendorSkuMapping,
} from "store/vendor-items/vendorItemsSlice";
import { getPurchaseCodes } from "store/purchase-codes/purchaseCodesSlice";

const MESSAGE_KEY = "vendor_mapping";

interface SkuScoreEntry {
  skuCode: string;
  name: string;
  reliabilityScore: number;
}

const VendorItemsMappingPage = () => {
  useDocumentTitle("Vendor SKU Mappings");
  const dispatch = useAppDispatch();
  const { vendorId } = useParams<{ vendorId: string }>();
  const { state } = useLocation();
  const vendor = state as IVendorB2c | null;
  const numericVendorId = parseInt(vendorId ?? "0", 10);

  const { mappings, status, error } = useAppSelector((s) => s.vendorItems);
  const { purchaseCodes, status: skuStatus } = useAppSelector((s) => s.purchaseCodes);

  // Per-SKU score entries for the create form
  const [skuEntries, setSkuEntries] = useState<SkuScoreEntry[]>([]);

  // Inline edit state for existing mappings
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editScore, setEditScore] = useState<number>(0);

  const fetchMappings = useCallback(() => {
    if (numericVendorId) dispatch(getVendorSkuMappings(numericVendorId));
  }, [dispatch, numericVendorId]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  useEffect(() => {
    dispatch(getPurchaseCodes());
  }, [dispatch]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading mappings...", key: MESSAGE_KEY });
    } else if (status === "saving") {
      message.loading({ content: "Saving...", key: MESSAGE_KEY });
    } else if (status === "deleting") {
      message.loading({ content: "Deleting...", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    } else {
      message.destroy(MESSAGE_KEY);
    }
    return () => {
      message.destroy(MESSAGE_KEY);
    };
  }, [status, error]);

  const alreadyMappedCodes = useMemo(
    () => new Set(mappings.map((m) => m.purchaseSkuCode)),
    [mappings]
  );

  const handleSkuSelect = (codes: string[]) => {
    // Strip already-mapped codes — they live in the table below, not in skuEntries
    const newCodes = codes.filter((c) => !alreadyMappedCodes.has(c));
    setSkuEntries((prev) => {
      const existingMap = new Map(prev.map((item) => [item.skuCode, item]));
      return newCodes.map((code) => {
        if (existingMap.has(code)) return existingMap.get(code)!;
        const sku = (purchaseCodes ?? []).find((s) => s.skuCode === code);
        return { skuCode: code, name: sku?.name ?? code, reliabilityScore: 0 };
      });
    });
  };

  const handleScoreChange = (code: string, val: number | null) => {
    setSkuEntries((prev) =>
      prev.map((item) =>
        item.skuCode === code ? { ...item, reliabilityScore: val ?? 0 } : item
      )
    );
  };

  const handleCreate = async () => {
    if (skuEntries.length === 0) {
      message.warning("Please select at least one Procurement SKU Code.");
      return;
    }
    const result = await dispatch(
      createVendorSkuMapping({
        vendorId: numericVendorId,
        skuMappings: skuEntries.map((e) => ({
          purchaseSkuCode: e.skuCode,
          reliabilityScore: e.reliabilityScore,
        })),
      })
    );
    if (createVendorSkuMapping.fulfilled.match(result)) {
      message.success({
        content: `${skuEntries.length} mapping${skuEntries.length > 1 ? "s" : ""} created!`,
        key: MESSAGE_KEY,
      });
      setSkuEntries([]);
      fetchMappings();
    }
  };

  const handleSaveEdit = async (mapping: IVendorSkuMapping) => {
    const result = await dispatch(
      updateVendorSkuMappingReliability({
        vendorId: mapping.vendorId,
        purchaseCode: mapping.purchaseSkuCode,
        reliabilityScore: editScore,
      })
    );
    if (updateVendorSkuMappingReliability.fulfilled.match(result)) {
      message.success({ content: "Reliability score updated!", key: MESSAGE_KEY });
      setEditingKey(null);
      fetchMappings();
    }
  };

  const handleDelete = async (mapping: IVendorSkuMapping) => {
    const result = await dispatch(
      deleteVendorSkuMapping({
        vendorId: mapping.vendorId,
        purchaseCode: mapping.purchaseSkuCode,
      })
    );
    if (deleteVendorSkuMapping.fulfilled.match(result)) {
      message.success({ content: "Mapping deleted!", key: MESSAGE_KEY });
      fetchMappings();
    }
  };

  const skuCodeOptions = (purchaseCodes ?? []).map((sku) => ({
    value: sku.skuCode,
    label: `${sku.skuCode} - ${sku.name}`,
  }));

  // Combine already-mapped codes (non-removable) with newly selected codes
  const selectValue = [
    ...Array.from(alreadyMappedCodes),
    ...skuEntries.map((e) => e.skuCode),
  ];

  const skuEntryColumns: ColumnsType<SkuScoreEntry> = [
    {
      key: "skuCode",
      title: "SKU Code",
      dataIndex: "skuCode",
      width: 160,
    },
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
    },
    {
      key: "reliabilityScore",
      title: "Reliability Score",
      dataIndex: "reliabilityScore",
      width: 180,
      render: (_, entry) => (
        <InputNumber
          min={0}
          max={10}
          value={entry.reliabilityScore}
          onChange={(val) => handleScoreChange(entry.skuCode, val)}
          style={{ width: "100%" }}
          placeholder="0–10"
        />
      ),
    },
  ];

  const mappingColumns: ColumnsType<IVendorSkuMapping> = [
    {
      key: "purchaseSkuCode",
      title: "Procurement SKU Code",
      dataIndex: "purchaseSkuCode",
    },
    {
      key: "reliabilityScore",
      title: "Reliability Score",
      dataIndex: "reliabilityScore",
      render: (_, mapping) =>
        editingKey === mapping.purchaseSkuCode ? (
          <InputNumber
            min={0}
            max={10}
            value={editScore}
            onChange={(v) => setEditScore(v ?? 0)}
            style={{ width: 100 }}
          />
        ) : (
          <>{mapping.reliabilityScore}</>
        ),
    },
    {
      key: "actions",
      title: "Actions",
      render: (_, record) =>
        editingKey === record.purchaseSkuCode ? (
          <Row align="middle" style={{ gap: "0.5rem" }}>
            <Button
              type="link"
              size="small"
              onClick={() => handleSaveEdit(record)}
              disabled={status === "saving"}
            >
              Save
            </Button>
            <Button type="link" size="small" onClick={() => setEditingKey(null)}>
              Cancel
            </Button>
          </Row>
        ) : (
          <Row align="middle" style={{ gap: "0.5rem", flexWrap: "nowrap" }}>
            <Col>
              <Button
                shape="circle"
                size="small"
                icon={<MdEdit size={14} />}
                onClick={() => {
                  setEditingKey(record.purchaseSkuCode);
                  setEditScore(record.reliabilityScore);
                }}
                disabled={editingKey !== null || status === "deleting"}
              />
            </Col>
            <Col>
              <Popconfirm
                title={`Delete mapping "${record.purchaseSkuCode}"?`}
                okText="Delete"
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                onConfirm={() => handleDelete(record)}
                disabled={editingKey !== null || status === "deleting"}
              >
                <Button
                  shape="circle"
                  size="small"
                  danger
                  icon={<MdDeleteOutline size={14} />}
                  disabled={editingKey !== null || status === "deleting"}
                />
              </Popconfirm>
            </Col>
          </Row>
        ),
    },
  ];

  return (
    <>
      {/* Breadcrumb */}
      <Row style={{ marginBottom: "1rem" }}>
        <Col xs={24}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <NavLink to={`/${routes.PO.VENDOR_ITEMS}`}>Vendor Items</NavLink>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{vendor?.name ?? `Vendor ${vendorId}`}</Breadcrumb.Item>
          </Breadcrumb>
        </Col>
      </Row>

      {/* Header */}
      <Row>
        <Col>
          <AppPageHeader title="Vendor SKU Mappings" />
        </Col>
      </Row>

      {/* Vendor Info */}
      {vendor && (
        <>
          <Form layout="vertical" style={{ marginBottom: "0.5rem", marginTop: "1rem" }}>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12} md={4}>
                <Form.Item label="Vendor Code">
                  <Input value={vendor.vendorCode} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Form.Item label="Name">
                  <Input value={vendor.name} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Form.Item label="POC Name">
                  <Input value={vendor.pocName ?? ""} disabled placeholder="-" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Form.Item label="Phone">
                  <Input value={vendor.primaryPhone ?? ""} disabled placeholder="-" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Form.Item label="Email">
                  <Input value={vendor.email ?? ""} disabled placeholder="-" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={2}>
                <Form.Item label="Status">
                  <div style={{ paddingTop: "5px" }}>
                    {vendor.status === "ACTIVE" ? (
                      <Tag color="green">Active</Tag>
                    ) : (
                      <Tag color="default">{vendor.status ?? "-"}</Tag>
                    )}
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Divider style={{ margin: "0 0 1.5rem" }} />
        </>
      )}

      {/* Create Mapping Form */}
      <Form layout="vertical" style={{ marginBottom: "1.5rem" }}>
        <Form.Item label="Procurement SKU Code" required>
          <Select
            mode="multiple"
            showSearch
            placeholder="Search by SKU code or name..."
            options={skuCodeOptions}
            value={selectValue}
            onChange={handleSkuSelect}
            filterOption={(input, option) =>
              option?.label?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
            }
            tagRender={(props) => {
              const isAlreadyMapped = alreadyMappedCodes.has(props.value as string);
              return (
                <Tag
                  color={isAlreadyMapped ? "default" : "blue"}
                  closable={!isAlreadyMapped}
                  onClose={isAlreadyMapped ? undefined : props.onClose}
                  style={{ marginRight: 4 }}
                >
                  {props.label}
                </Tag>
              );
            }}
            loading={skuStatus === "loading"}
            style={{ width: "100%" }}
          />
        </Form.Item>

        {skuEntries.length > 0 && (
          <>
            <Table<SkuScoreEntry>
              columns={skuEntryColumns}
              dataSource={skuEntries}
              rowKey="skuCode"
              size="small"
              bordered
              pagination={false}
              style={{ marginBottom: "1rem" }}
            />
            <Row justify="end">
              <Col>
                <Button
                  type="primary"
                  onClick={handleCreate}
                  loading={status === "saving"}
                  disabled={status === "saving"}
                >
                  Create{skuEntries.length > 1 ? ` (${skuEntries.length} Mappings)` : " Mapping"}
                </Button>
              </Col>
            </Row>
          </>
        )}
      </Form>

      <Divider style={{ margin: "0 0 1.5rem" }} />

      {/* Existing Mappings Table */}
      <Row>
        <Col xs={24}>
          <Table<IVendorSkuMapping>
            columns={mappingColumns}
            dataSource={mappings}
            rowKey="purchaseSkuCode"
            size="small"
            bordered
            pagination={false}
            loading={status === "loading"}
            locale={{ emptyText: "No SKU mappings found for this vendor." }}
          />
        </Col>
      </Row>
    </>
  );
};

export default VendorItemsMappingPage;
